import type { CSSProperties, ReactElement } from 'react';
import { Lien } from '../../app/navigation.js';
import { Pastille } from '../../app/pastille.js';
import { CIBLE_MIN, COULEURS, OMBRES, RAYONS, styleBouton } from '../../app/styles.js';
import {
  MENTION_ESTIMATION,
  ancienneteJours,
  compterParStatut,
  filtrerParStatut,
  formaterEuros,
  indicateurs,
  libelleValeur,
  trierDossiers,
  type DonneesPilotage,
  type LignePilotage,
} from './modele.js';
import { presenterBlocage } from '../dossier/blocages.js';
import { REFERENTIEL_PILOTAGE } from './referentiel.js';

export interface PropsVuePilotage {
  donnees: DonneesPilotage;
  filtreStatut: string | null;
  aujourdhui: string;
  largeur: number;
  onFiltre: (code: string | null) => void;
  /** `haut` : indicateurs, filtres, tableau ; `bas` : encaissements et équipe (la coquille place les décisions entre les deux). */
  partie?: 'tout' | 'haut' | 'bas';
}

/** Adresse de l'écran Dossier (D03) : la coquille l'ouvre avec `?dossier=<id>`. */
const CHEMIN_DOSSIER = '/dossier';
const SEUIL_BUREAU = 768;

const sansMarge: CSSProperties = { margin: 0 };
const carte: CSSProperties = {
  border: `1px solid ${COULEURS.bordureDouce}`,
  borderRadius: RAYONS.carte,
  padding: 16,
  background: COULEURS.blanc,
  boxShadow: OMBRES.carte,
};
const titreSection: CSSProperties = { ...sansMarge, fontSize: 18, color: COULEURS.marine };
const lienDossier: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: CIBLE_MIN,
  color: COULEURS.marine,
  fontWeight: 600,
};
const cellule: CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  verticalAlign: 'top',
  borderBottom: `1px solid ${COULEURS.bordureDouce}`,
};

function Indicateur({
  cle,
  titre,
  valeur,
  detail,
  couleur = COULEURS.marine,
}: {
  cle: string;
  titre: string;
  valeur: string;
  detail?: string;
  couleur?: string;
}): ReactElement {
  return (
    <div data-indicateur={cle} style={{ ...carte, flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <p style={{ ...sansMarge, color: COULEURS.ardoise }}>{titre}</p>
      <p
        data-chiffre=""
        style={{ ...sansMarge, fontSize: 30, lineHeight: 1.1, fontWeight: 600, color: couleur, fontVariantNumeric: 'tabular-nums' }}
      >
        {valeur}
      </p>
      {detail ? <p style={{ ...sansMarge, fontSize: 14, color: COULEURS.ardoise }}>{detail}</p> : null}
    </div>
  );
}

function Blocage({ ligne, aujourdhui }: { ligne: LignePilotage; aujourdhui: string }): ReactElement {
  if (!ligne.blocker) return <span>—</span>;
  return (
    <Pastille ton="bloque" data-blocage="">
      {`${presenterBlocage(ligne.blocker).libelle} · ${ancienneteJours(ligne.blocker.since, aujourdhui)} j`}
    </Pastille>
  );
}

function Statut({ ligne }: { ligne: LignePilotage }): ReactElement | null {
  if (!ligne.statutMetier) return null;
  const compteRendu = ligne.statutMetier.code === REFERENTIEL_PILOTAGE.codeCompteRendu;
  return (
    <Pastille ton={compteRendu ? 'attention' : 'equipe'} {...(compteRendu ? { 'data-compte-rendu': 'true' } : {})}>
      {ligne.statutMetier.libelle}
    </Pastille>
  );
}

function Dossier({ ligne }: { ligne: LignePilotage }): ReactElement {
  return (
    <>
      <Lien vers={`${CHEMIN_DOSSIER}?dossier=${encodeURIComponent(ligne.id)}`} style={lienDossier} enfants={ligne.title} />
      <br />
      <small style={{ color: COULEURS.ardoise }}>
        {[ligne.typeDossier?.libelle, `${ligne.heirsCount} héritier${ligne.heirsCount > 1 ? 's' : ''}`].filter(Boolean).join(' · ')}
      </small>
    </>
  );
}

function Tableau({ lignes, aujourdhui }: { lignes: readonly LignePilotage[]; aujourdhui: string }): ReactElement {
  const avecStatut = lignes.some((l) => l.statutMetier);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-mise-en-page="tableau" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <caption style={{ position: 'absolute', left: -9999 }}>Dossiers, du plus bloqué au plus avancé</caption>
        <thead>
          <tr>
            {['Dossier', ...(avecStatut ? ['Statut'] : []), 'Phase', 'Blocage'].map((t) => (
              <th key={t} scope="col" style={{ ...cellule, color: COULEURS.ardoise }}>
                {t}
              </th>
            ))}
            {/* Valeur présumée du bien saisie à l'ouverture (le mot d'Isabelle) : ni un coût, ni l'estimation des parts (lot 3, point 3). */}
            <th scope="col" style={{ ...cellule, color: COULEURS.ardoise }}>
              Estimation de départ
              <span style={{ display: 'block', fontWeight: 400, fontSize: 12 }}>valeur présumée du bien</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.id} data-dossier={l.id}>
              <td style={cellule}>
                <Dossier ligne={l} />
              </td>
              {avecStatut ? (
                <td style={cellule}>
                  <Statut ligne={l} />
                </td>
              ) : null}
              <td style={cellule}>{l.currentPhase}</td>
              <td style={cellule}>
                <Blocage ligne={l} aujourdhui={aujourdhui} />
              </td>
              <td style={cellule}>{libelleValeur(l)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cartes({ lignes, aujourdhui }: { lignes: readonly LignePilotage[]; aujourdhui: string }): ReactElement {
  return (
    <ul data-mise-en-page="cartes" style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {lignes.map((l) => (
        <li key={l.id} data-dossier={l.id} style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Dossier ligne={l} />
          <Statut ligne={l} />
          <span>{`Phase ${l.currentPhase}`}</span>
          <Blocage ligne={l} aujourdhui={aujourdhui} />
          <span>{libelleValeur(l)}</span>
        </li>
      ))}
    </ul>
  );
}

/** D01, corps du tableau de bord d'Isabelle : indicateurs, statuts métier, dossiers du plus bloqué au plus avancé, équipe. */
export function VuePilotage({ donnees, filtreStatut, aujourdhui, largeur, onFiltre, partie = 'tout' }: PropsVuePilotage): ReactElement {
  const { tableau } = donnees;
  const ind = indicateurs(tableau.dossiers);
  const { statuts, disponible } = compterParStatut(tableau.dossiers, tableau.statuts);
  const actifs = tableau.dossiers.filter((l) => l.status !== 'clos' && l.status !== 'abandonne');
  const lignes = trierDossiers(filtreStatut === null ? actifs : filtrerParStatut(tableau.dossiers, filtreStatut));
  const avecValeur = lignes.some((l) => l.estimatedValue !== null);
  const messageVide = filtreStatut === null ? 'Aucun dossier actif pour le moment.' : 'Aucun dossier ne porte ce statut.';
  return (
    <div data-vue="pilotage" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {partie !== 'bas' ? (
        <>
          <section aria-label="Indicateurs" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <Indicateur cle="actifs" titre="Dossiers actifs" valeur={String(ind.actifs)} />
            <Indicateur
              cle="bloques"
              titre="Dossiers bloqués"
              valeur={String(ind.bloques)}
              couleur={COULEURS.rouge}
              {...(ind.parNature.length > 0 ? { detail: ind.parNature.map((n) => `${n.libelle} ${n.nombre}`).join(' · ') } : {})}
            />
            <Indicateur cle="encaisser" titre="Reste à encaisser" valeur={formaterEuros(tableau.outstanding.amountCents)} />
            <Indicateur
              cle="decisions"
              titre="Décisions en attente"
              valeur={String(tableau.pendingDecisions)}
              couleur={COULEURS.orangeBouton}
              detail="réservées à vous"
            />
          </section>

          <section aria-labelledby="statuts-titre" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h2 id="statuts-titre" style={titreSection}>
              Dossiers par statut
            </h2>
            {disponible ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {statuts.map((s) => (
                  <button
                    key={s.code}
                    type="button"
                    data-statut={s.code}
                    aria-pressed={filtreStatut === s.code}
                    style={{
                      ...styleBouton,
                      borderRadius: 999,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      ...(filtreStatut === s.code ? { background: COULEURS.marine, color: COULEURS.blanc } : {}),
                    }}
                    onClick={() => onFiltre(s.code)}
                  >
                    <span>{s.libelle}</span>
                    <strong>{s.nombre}</strong>
                  </button>
                ))}
              </div>
            ) : (
              <p style={sansMarge}>Les statuts des dossiers ne sont pas encore transmis par le serveur.</p>
            )}
          </section>

          <section aria-labelledby="dossiers-titre" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h2 id="dossiers-titre" style={titreSection}>
              Dossiers, du plus bloqué au plus avancé
            </h2>
            {lignes.length === 0 ? (
              <p style={sansMarge}>{messageVide}</p>
            ) : largeur >= SEUIL_BUREAU ? (
              <Tableau lignes={lignes} aujourdhui={aujourdhui} />
            ) : (
              <Cartes lignes={lignes} aujourdhui={aujourdhui} />
            )}
            {avecValeur ? <p style={{ ...sansMarge, fontSize: 14, color: COULEURS.ardoise }}>{MENTION_ESTIMATION}</p> : null}
          </section>
        </>
      ) : null}
      {partie !== 'haut' ? (
        <>
          <section aria-labelledby="encaissements-titre" style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h2 id="encaissements-titre" style={titreSection}>
              Encaissements
            </h2>
            <p style={sansMarge}>{`Reste à encaisser : ${formaterEuros(tableau.outstanding.amountCents)}`}</p>
            <p style={{ ...sansMarge, color: COULEURS.ardoise }}>{`Déjà encaissé : ${formaterEuros(tableau.collected.amountCents)}`}</p>
          </section>

          <section aria-labelledby="equipe-titre" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h2 id="equipe-titre" style={titreSection}>
              Activité de l’équipe
            </h2>
            {tableau.teamActivity.length === 0 ? (
              <p style={sansMarge}>Aucune activité enregistrée aujourd’hui.</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {tableau.teamActivity.map((a) => (
                  <li
                    key={a.userDisplayName}
                  >{`${a.userDisplayName} : ${a.actionsToday} action${a.actionsToday > 1 ? 's' : ''} aujourd’hui`}</li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
